import { CSSProperties, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Sidebar from "@rio-cloud/rio-uikit/Sidebar";
import moment from "moment";
import {
  getMaintenanceAlertDetail,
  getMaintenanceAlertHistory,
  MAINTENANCE_ALERT_TYPES,
  MaintenanceAlert,
  MaintenanceAlertDetail,
  MaintenanceAlertType,
} from "@services/maintenance";
import { DashboardFilter } from "../../../types/dashboard";
import AlertsHistoryListSkeleton, {
  AlertsHistoryPaginationSkeleton,
} from "@components/Skeletons/AlertsHistoryListSkeleton";
import MaintenanceAlertDetailSkeleton from "@components/Skeletons/MaintenanceAlertDetailSkeleton";
import Pager from "@components/Maintenance/Pager";
import { SEVERITY_META } from "@components/Maintenance/AlertSeverity";
import { useT } from "@hooks";

export type MaintenanceAlertsFilter = "ALL" | MaintenanceAlertType;

type Translate = ReturnType<typeof useT>;

const PAGE_SIZE = 10;
const SIDEBAR_WIDTH = 600;
const HISTORY_MAX_HEIGHT = 560;
const HISTORY_DATE_SKELETON_WIDTHS = [67];

const FILTER_KEYS: MaintenanceAlertsFilter[] = ["ALL", ...MAINTENANCE_ALERT_TYPES];

const METRIC_LABEL_KEYS: Record<MaintenanceAlertType, string[]> = {
  CORRECTIVE_CONCENTRATION: ["maintenance.alerts.metric.otherMaintenance", "maintenance.alerts.metric.corrective"],
  COST_PER_KM_INCREASE: ["maintenance.alerts.metric.previousCostPerKm", "maintenance.alerts.metric.currentCostPerKm"],
  COST_PER_VEHICLE_DECREASE: [
    "maintenance.alerts.metric.previousAverageCost",
    "maintenance.alerts.metric.currentAverageCost",
  ],
  BUDGET_EXCEEDED: ["maintenance.alerts.metric.forecast", "maintenance.alerts.metric.realized"],
};

const RESET_ROW: CSSProperties = {
  background: "none",
  borderStyle: "none",
  padding: 0,
  textAlign: "inherit",
  width: "100%",
};

type TabKey = "historico" | "detalhes";

interface MaintenanceAlertsSidebarProps {
  open: boolean;
  onClose: () => void;
  initialFilter?: MaintenanceAlertsFilter;
  period: DashboardFilter;
}

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (value?: string | null) => (value ? moment(value).format("DD/MM/YYYY") : "-");

const toAlertType = (filter: MaintenanceAlertsFilter): MaintenanceAlertType | undefined =>
  filter === "ALL" ? undefined : filter;

const alertTitle = (alert: MaintenanceAlert, t: Translate) =>
  MAINTENANCE_ALERT_TYPES.includes(alert.type) ? t(`maintenance.alerts.type.${alert.type}`) : alert.title;

const metricLabel = (type: MaintenanceAlertType, index: number, fallback: string, t: Translate) => {
  const key = METRIC_LABEL_KEYS[type]?.[index];
  return key ? t(key) : fallback;
};

const isActivationKey = (key: string) => key === "Enter" || key === " ";

interface HistoryItemsProps {
  isLoading: boolean;
  items: MaintenanceAlert[];
  onSelectAlert: (alertId: string) => void;
}

const HistoryItems = ({ isLoading, items, onSelectAlert }: Readonly<HistoryItemsProps>) => {
  const t = useT();

  if (isLoading) {
    return <AlertsHistoryListSkeleton chipVariant="inlineTitle" titleWidths={HISTORY_DATE_SKELETON_WIDTHS} />;
  }

  if (items.length === 0) {
    return (
      <div className="display-flex align-items-center justify-content-center padding-20">
        <span className="text-color-gray text-size-14">{t("maintenance.alerts.empty")}</span>
      </div>
    );
  }

  return (
    <>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <button
            key={item.id}
            type="button"
            style={RESET_ROW}
            tabIndex={0}
            onClick={() => onSelectAlert(item.id)}
            onKeyDown={(event) => {
              if (isActivationKey(event.key)) {
                event.preventDefault();
                onSelectAlert(item.id);
              }
            }}
            className={`display-flex flex-column gap-5 padding-y-15 padding-right-20 cursor-pointer ${
              isLast ? "" : "border border-style-solid border-bottom-only border-width-1 border-color-light"
            }`}
          >
            <div className="display-flex align-items-start justify-content-between gap-15 width-100pct">
              <span className="text-size-14 text-medium flex-0 white-space-nowrap">{formatDate(item.occurredAt)}</span>
              <span className="text-color-darker text-size-14 text-right min-width-0">{alertTitle(item, t)}</span>
            </div>
            <span className="text-color-gray text-size-14">{item.description}</span>
          </button>
        );
      })}
    </>
  );
};

const DetailContent = ({ detail }: Readonly<{ detail: MaintenanceAlertDetail }>) => {
  const t = useT();
  const meta = SEVERITY_META[detail.severity];

  return (
    <>
      <div className="display-flex flex-wrap align-items-start justify-content-between gap-15">
        <div className="position-relative flex-1-1 display-flex align-items-center gap-10 padding-10 rounded">
          <span
            className={`position-absolute top-0 left-0 width-100pct height-100pct rounded opacity-10 bg-${meta.color}`}
            aria-hidden="true"
          />
          <span className={`position-relative rioglyph ${meta.icon} text-color-${meta.color} text-size-18`} />
          <span className={`position-relative text-medium text-size-12 text-uppercase text-color-${meta.color}`}>
            {alertTitle(detail, t)}
          </span>
        </div>
        <div className="display-flex flex-column gap-5">
          <span className="text-color-gray text-size-12 text-medium text-uppercase">
            {t("maintenance.alerts.date")}
          </span>
          <span className="text-size-14 text-medium">{formatDate(detail.occurredAt)}</span>
        </div>
      </div>

      <div className="margin-top-15 padding-15 rounded border border-width-1 border-color-light bg-white">
        <span className="text-color-gray text-size-14">{detail.description}</span>
      </div>

      <hr className="margin-top-20 margin-bottom-0 border-color-light" />

      <span className="display-block margin-top-20 text-color-gray text-size-12 text-medium text-uppercase">
        {t("maintenance.alerts.section.data")}
      </span>

      <div className="display-flex flex-wrap gap-10 margin-top-15">
        {detail.metrics.map((metric, index) => {
          const borderClass = metric.highlighted ? "" : "border border-width-1 border-color-light";
          const textColor = metric.highlighted ? `text-color-${meta.color}` : "";
          return (
            <div
              key={metric.label}
              data-testid="metrica"
              className={`position-relative flex-1-1 display-flex flex-column justify-content-center gap-5 padding-15 height-80 rounded overflow-hidden bg-white ${borderClass}`}
            >
              {metric.highlighted && (
                <span
                  className={`position-absolute top-0 left-0 width-100pct height-100pct opacity-10 bg-${meta.color}`}
                  aria-hidden="true"
                />
              )}
              <span
                className={`position-relative text-size-12 text-medium text-uppercase ${textColor || "text-color-gray"}`}
              >
                {metricLabel(detail.type, index, metric.label, t)}
              </span>
              <span className={`position-relative text-size-20 text-medium ${textColor}`.trim()}>
                {formatCurrency(metric.value)}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
};

interface DetailTabProps {
  isLoading: boolean;
  detail: MaintenanceAlertDetail | null;
}

const DetailTab = ({ isLoading, detail }: Readonly<DetailTabProps>) => {
  const t = useT();

  if (isLoading) {
    return <MaintenanceAlertDetailSkeleton />;
  }

  if (detail) {
    return <DetailContent detail={detail} />;
  }

  return (
    <div className="flex-1-1 display-flex flex-column align-items-center justify-content-center text-center gap-10">
      <span className="text-color-gray text-size-20 text-medium">{t("maintenance.alerts.noAlertSelected")}</span>
      <span className="text-color-gray text-size-14 text-normal">
        {t("maintenance.alerts.noAlertSelectedDescription")}
      </span>
    </div>
  );
};

export default function MaintenanceAlertsSidebar({
  open,
  onClose,
  initialFilter = "ALL",
  period,
}: Readonly<MaintenanceAlertsSidebarProps>) {
  const t = useT();

  const filterLabel = (filter: MaintenanceAlertsFilter) =>
    filter === "ALL" ? t("maintenance.alerts.filter.all") : t(`maintenance.alerts.filter.${filter}`);

  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("historico");
  const [activeFilter, setActiveFilter] = useState<MaintenanceAlertsFilter>("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [items, setItems] = useState<MaintenanceAlert[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [alertDetail, setAlertDetail] = useState<MaintenanceAlertDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const fetchAlertDetail = useCallback(
    async (alertId: string) => {
      setIsLoadingDetail(true);
      setAlertDetail(null);
      setActiveTab("detalhes");
      try {
        const detail = await getMaintenanceAlertDetail(period, alertId);
        setAlertDetail(detail);
      } catch {
        setAlertDetail(null);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [period]
  );

  const fetchHistory = useCallback(
    async (filter: MaintenanceAlertsFilter, page: number) => {
      setIsLoading(true);
      try {
        const response = await getMaintenanceAlertHistory(period, toAlertType(filter), page - 1, PAGE_SIZE);
        setItems(response.content);
        setTotalPages(Math.max(1, response.totalPages));
      } catch {
        setItems([]);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    },
    [period]
  );

  useEffect(() => {
    setContainer(document.getElementById("app-right-sidebar-slot") ?? document.body);
  }, []);

  useEffect(() => {
    if (!open) return;
    setActiveTab("historico");
    setActiveFilter(initialFilter);
    setCurrentPage(1);
    setAlertDetail(null);
    fetchHistory(initialFilter, 1);
  }, [open, initialFilter, fetchHistory]);

  const handleFilterChange = (filter: MaintenanceAlertsFilter) => {
    if (filter === activeFilter) return;
    setActiveFilter(filter);
    setCurrentPage(1);
    fetchHistory(filter, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchHistory(activeFilter, page);
  };

  if (!container) return null;

  const isDetailEmpty = activeTab === "detalhes" && !isLoadingDetail && !alertDetail;

  return createPortal(
    <Sidebar
      fly
      hasBackdrop
      makeBackdropVisible
      width={SIDEBAR_WIDTH}
      title={t("maintenance.alerts.title")}
      titleClassName="padding-left-10"
      position={Sidebar.RIGHT}
      closed={!open}
      onClose={onClose}
      onBackdropClick={onClose}
    >
      <div className="display-flex margin-top-15 border border-style-solid border-top-only border-width-1 border-color-light">
        <div className="width-20 border border-style-solid border-bottom-only border-width-2 border-color-light" />
        {(["historico", "detalhes"] as const).map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              tabIndex={0}
              onClick={() => setActiveTab(tab)}
              onKeyDown={(event) => {
                if (isActivationKey(event.key)) setActiveTab(tab);
              }}
              style={{ background: "none" }}
              className={`padding-y-10 padding-x-15 text-size-14 text-medium cursor-pointer border border-style-solid border-bottom-only border-width-2 user-select-none ${
                isActive ? "text-color-primary border-color-primary" : "text-color-gray border-color-light"
              }`}
            >
              {tab === "historico" ? t("maintenance.alerts.tab.history") : t("maintenance.alerts.tab.details")}
            </button>
          );
        })}
        <div className="flex-1-1 border border-style-solid border-bottom-only border-width-2 border-color-light" />
      </div>
      <div className={`padding-20 bg-highlight-decent ${isDetailEmpty ? "flex-1-1 display-flex flex-column" : ""}`}>
        {activeTab === "historico" ? (
          <>
            <div className="display-flex gap-10 flex-wrap">
              {FILTER_KEYS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`btn btn-primary ${activeFilter === filter ? "" : "btn-outline"}`.trim()}
                  onClick={() => handleFilterChange(filter)}
                >
                  {filterLabel(filter)}
                </button>
              ))}
            </div>
            <div
              className="display-flex flex-column margin-top-20 overflow-y-auto"
              style={{ maxHeight: HISTORY_MAX_HEIGHT }}
            >
              <HistoryItems isLoading={isLoading} items={items} onSelectAlert={fetchAlertDetail} />
            </div>
            {isLoading && <AlertsHistoryPaginationSkeleton />}
            {!isLoading && items.length > 0 && (
              <Pager
                className="margin-top-15"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        ) : (
          <DetailTab isLoading={isLoadingDetail} detail={alertDetail} />
        )}
      </div>
    </Sidebar>,
    container
  );
}
